import {helpdeskQueries} from '@app/dashboard/helpdesk-queries';
import {helpCenterQueries} from '@app/help-center/help-center-queries';
import {authGuard, AuthRoute} from '@common/auth/guards/auth-route';
import {auth} from '@common/auth/use-auth';
import {queryClient} from '@common/http/query-client';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {searchParamsFromUrl} from '@ui/utils/urls/search-params-from-url';
import {Params, replace, RouteObject} from 'react-router';

// Customization (change AV): when the help center is disabled
// (settings.hc.disabled), the knowledge-base routes redirect away. Returned from
// a loader so it runs before the page renders / data is fetched. The customer
// ticket portal (/hc/tickets*) intentionally does NOT use this. See
// _changes_3.0.8.php.
const helpCenterDisabledRedirect = () =>
  getBootstrapData().settings.hc?.disabled ? replace('/') : null;

const articlePageLoader = async (params: Params) => {
  const disabled = helpCenterDisabledRedirect();
  if (disabled) return disabled;
  return await queryClient.ensureQueryData(
    helpCenterQueries.articles.getForArticlePage({
      articleId: params.articleId!,
      categoryId: params.categoryId,
      sectionId: params.sectionId,
    }),
  );
};

const categoryPageLoader = async (params: Params) => {
  const disabled = helpCenterDisabledRedirect();
  if (disabled) return disabled;
  return await queryClient.ensureQueryData(
    helpCenterQueries.categories.get(params.sectionId || params.categoryId!),
  );
};

// Multilingual sites: locale-prefixed, slug-based, hierarchical client routes
// (/{locale}/hc/{cat}[/{sec}[/{art}]]) so a shared/direct localized URL hydrates
// client-side (reads the server bootstrap loader) and in-app navigation works.
// One route group per configured locale; the locale rides on the route handle.
// Sites with no configured localizations get none (legacy /hc routes only).
const localizedHcRoutes: RouteObject[] = getBootstrapData().i18n.locales.map(
  loc => ({
    path: `${loc.language}/hc`,
    handle: {hcLocale: loc.language},
    element: <AuthRoute requireLogin={false} permission="articles.view" />,
    children: [
      {
        index: true,
        lazy: () => import('@app/help-center/homepage/hc-landing-page'),
        loader: async () =>
          helpCenterDisabledRedirect() ??
          queryClient.ensureQueryData(
            helpCenterQueries.categories.landingPageDataLocalized(
              loc.language,
            ),
          ),
      },
      {
        path: ':hcCategorySlug',
        lazy: () => import('@app/help-center/categories/category-page'),
        loader: async ({params}: {params: Params}) =>
          helpCenterDisabledRedirect() ??
          queryClient.ensureQueryData(
            helpCenterQueries.categories.getLocalized(
              loc.language,
              params.hcCategorySlug!,
            ),
          ),
      },
      {
        path: ':hcCategorySlug/:hcSectionSlug',
        lazy: () => import('@app/help-center/categories/category-page'),
        loader: async ({params}: {params: Params}) =>
          helpCenterDisabledRedirect() ??
          queryClient.ensureQueryData(
            helpCenterQueries.categories.getLocalized(
              loc.language,
              params.hcCategorySlug!,
              params.hcSectionSlug,
            ),
          ),
      },
      {
        path: ':hcCategorySlug/:hcSectionSlug/:hcArticleSlug',
        lazy: () =>
          import('@app/help-center/articles/article-page/article-page'),
        loader: async ({params}: {params: Params}) =>
          helpCenterDisabledRedirect() ??
          queryClient.ensureQueryData(
            helpCenterQueries.articles.getLocalizedArticlePage(
              loc.language,
              params.hcCategorySlug!,
              params.hcSectionSlug!,
              params.hcArticleSlug!,
            ),
          ),
      },
    ],
  }),
);

export const helpCenterRoutes: RouteObject[] = [
  ...localizedHcRoutes,
  {
    path: 'hc',
    element: <AuthRoute requireLogin={false} permission="articles.view" />,
    children: [
      {
        index: true,
        lazy: () => import('@app/help-center/homepage/hc-landing-page'),
        loader: async () => {
          const disabled = helpCenterDisabledRedirect();
          if (disabled) return disabled;
          // multilingual: the help center lives under a locale prefix, so send
          // the bare /hc to /{active-locale}/hc
          const {i18n} = getBootstrapData();
          if (i18n.locales.length && i18n.active) {
            return replace(`/${i18n.active}/hc`);
          }
          return queryClient.ensureQueryData(
            helpCenterQueries.categories.landingPageData(),
          );
        },
      },
      {
        path: 'articles/:articleId/:articleSlug',
        lazy: () =>
          import('@app/help-center/articles/article-page/article-page'),
        loader: async ({params}) => articlePageLoader(params),
      },
      {
        path: 'articles/:categoryId/:sectionId/:articleId',
        lazy: () =>
          import('@app/help-center/articles/article-page/article-page'),
        loader: async ({params}) => articlePageLoader(params),
      },
      {
        path: 'articles/:categoryId/:sectionId/:articleId/:articleSlug',
        lazy: () =>
          import('@app/help-center/articles/article-page/article-page'),
        loader: async ({params}) => articlePageLoader(params),
      },
      {
        path: 'categories/:categoryId/:sectionId/:slug',
        lazy: () => import('@app/help-center/categories/category-page'),
        loader: async ({params}) => categoryPageLoader(params),
      },
      {
        path: 'categories/:categoryId/:slug',
        lazy: () => import('@app/help-center/categories/category-page'),
        loader: async ({params}) => categoryPageLoader(params),
      },
      {
        path: 'search/:query',
        lazy: () => import('@app/help-center/search/hc-search-page'),
        loader: ({params}) =>
          helpCenterDisabledRedirect() ??
          queryClient.ensureQueryData(
            helpCenterQueries.articles.search({
              query: params.query,
              perPage: '30',
            }),
          ),
      },

      {
        path: 'tickets',
        lazy: () =>
          import(
            '@app/help-center/tickets-portal/ticklets-table/tickets-table-page'
          ),
        loader: async ({request}) => {
          const redirect = authGuard();
          if (redirect) return redirect;
          return Promise.all([
            await queryClient.ensureQueryData(
              helpCenterQueries.customerConversations.index(
                searchParamsFromUrl(request.url),
              ),
            ),
            await queryClient.ensureQueryData(
              helpdeskQueries.statuses.dropdownList('user'),
            ),
          ]);
        },
      },
      {
        path: 'tickets/new',
        lazy: () =>
          import(
            '@app/help-center/tickets-portal/new-ticket-page/new-ticket-page'
          ),
        loader: async () => {
          if (
            (!auth.isLoggedIn &&
              !getBootstrapData().settings.tickets?.guest_tickets) ||
            !auth.hasPermission('conversations.create')
          ) {
            return replace('/login');
          }
          return await queryClient.ensureQueryData(
            helpCenterQueries.customerConversations.newTicketPageData(),
          );
        },
      },
      {
        path: 'tickets/:conversationId',
        loader: async ({params}) => {
          const redirect = authGuard();
          if (redirect) return redirect;
          return Promise.all([
            queryClient.ensureQueryData(
              helpCenterQueries.customerConversations.get(
                params.conversationId!,
              ),
            ),
            queryClient.ensureInfiniteQueryData(
              helpdeskQueries.conversations.messages(params.conversationId!),
            ),
          ]);
        },
        lazy: () =>
          import('@app/help-center/tickets-portal/ticket-page/ticket-page'),
      },
    ],
  },
];
